import * as THREE from 'three';
import EventDispatcher from '../common/EventDispatcher';
import { Global } from '../common/global';
import RAPIER from '@dimforge/rapier3d-compat';
import gsap from 'gsap';

export default class GameScene {
  constructor(scene, camera) {
    this.scene = scene;
    this.camera = camera;
    this.onEvents = this.onEvents.bind(this);
    this._rigidBodies = [];
    this._debugMeshes = [];
  }

  init(gui) {
    this.postBar = null;
    this.events = EventDispatcher.getObj();
    this.physicsWorld = Global.physicsWorld;

    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents);
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents);
    this.events.addEventListener('GAME_EVENTS', this.onEvents);
  }

  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'create_scene':
        this.createScene();
        break;
      case 'update':
        this.update(data['message']['data']);
        break;
      case 'update_post_feature':
        this.updatePostFeature();
        break;
      case 'defend':
        this.showDefendEffects();
        break;
      case 'assign_position':
        this.stopDefendAnim();
        break;
      case 'dispose':
        this.dispose();
        break;
    }
  }

  addBoxCollider(x, y, z, w, h, d, options = {}) {
    if (!this.physicsWorld) return;

    // 1. Physics Body
    let bodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(x, y, z);
    let body = this.physicsWorld.createRigidBody(bodyDesc);

    let colliderDesc = RAPIER.ColliderDesc.cuboid(w / 2, h / 2, d / 2)
      .setFriction(options.friction ?? 0.5)
      .setRestitution(options.restitution ?? 0.2);

    // RESTORED: UserData is essential for identification in Ball.js
    if (options.name) {
      body.userData = { name: options.name };
    }

    this.physicsWorld.createCollider(colliderDesc, body);
    this._rigidBodies.push(body);

    // 2. RED VISUALIZER (Wireframe for debugging)
    const debugGeom = new THREE.BoxGeometry(w, h, d);
    const debugMat = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      wireframe: true,
      transparent: true,
      opacity: 0,
    });
    const debugMesh = new THREE.Mesh(debugGeom, debugMat);
    debugMesh.position.set(x, y, z);
    this.scene.add(debugMesh);
    this._debugMeshes.push(debugMesh);

    return body;
  }

  createScene() {
    this.addStadium();
  }

  addStadium() {
    this.stadium = Global.assets['glbs']['stadium']['asset'].scene.clone();
    this.scene.add(this.stadium);
    this.stadium.scale.set(
      Global.scaleFact,
      Global.scaleFact,
      Global.scaleFact
    );
    this.stadium.position.set(-0.23, 0, 0);

    this.stadium.traverse((item) => {
      if (!item.isMesh) return;

      if (item.name == 'Plane') {
        // item.material.depthWrite = true;
        // item.material.depthTest = false;
      } else {
        // item.material.depthWrite = true;
        // item.material.depthTest = false;
      }
      if (
        item.material &&
        (item.material.transparent || item.material.alphaTest > 0)
      ) {
        item.material.alphaTest = 0.2;
        item.material.depthWrite = true;
      }

      item.receiveShadow = true;
      item.castShadow = true;
      if (item.name === 'yellowedge') this.postBar = item;
      if (item.material) {
        item.material.envMap = Global.envMap;
      }

      if (item.material && item.material.name.includes('ground')) {
        item.material.envMapIntensity = 0.7;
      } else {
        item.material.envMapIntensity = 3;
      }
    });

    // this.setupCameraFlashes();
    this.setupPhysics();
  }

  setupPhysics() {
    if (!this.physicsWorld) return;
    const s = Global.scaleFact;

    // 1. GROUND
    this.addBoxCollider(0, -0.05, 0, 200, 0.1, 200, { name: 'floor' });

    // 2. BACK NET (Large catch-all area, no points)
    this.addBoxCollider(-0.15, 0.9 * s, -1.35 * s, 4.3 * s, 1.85 * s, 0.4, {
      name: 'goal_back_net',
    });

    // 3. SIDE POSTS (Solid Units)
    // Left Post
    this.addBoxCollider(-2.35 * s, 0.9 * s, -0.75 * s, 0.15, 1.8 * s, 1.3, {
      name: 'post_left',
      points: 50,
      restitution: 0.8,
    });

    // Right Post
    this.addBoxCollider(1.97 * s, 0.9 * s, -0.75 * s, 0.15, 1.8 * s, 1.3, {
      name: 'post_right',
      points: 50,
      restitution: 0.8,
    });

    // 4. TOP CROSSBAR

    const totalWidth = 4.4 * s;
    const barHeight = 1.82 * s;
    const barThickness = 0.15;

    // --- A. THE SCORING FRONTS (Shallow depth) ---
    const segmentCount = 8;
    const segW = totalWidth / segmentCount;
    const scoringDepth = 0.15; // Very thin, just for the front face detection
    const startX = -0.2 - totalWidth / 2 + segW / 2;
    // Positioned at the very front of the bar
    const frontZ = 0.1 * s - 0.02;

    for (let i = 0; i < segmentCount; i++) {
      const posX = startX + i * segW;
      const pointValue = i === 0 || i === segmentCount - 1 ? 100 : 20;

      this.addBoxCollider(
        posX,
        barHeight,
        frontZ,
        segW,
        barThickness,
        scoringDepth,
        {
          name: `bar_segment_${i}`,
          points: pointValue,
          restitution: 0.7,
        }
      );
    }

    // --- B. THE MAIN PHYSICAL BAR (Full size, behind segments) ---
    const mainBarDepth = 1.2; // The actual "meat" of the bar
    const mainBarZ = frontZ - mainBarDepth / 2 - scoringDepth / 2;

    this.addBoxCollider(
      -0.2, // Centered based on your startX logic
      barHeight,
      mainBarZ,
      totalWidth,
      barThickness,
      mainBarDepth,
      {
        name: 'main_crossbar_back', // No "segment" in name, so no points awarded
        restitution: 0.6,
      }
    );

    // 4. TOP CROSSBAR (Segmented for the stripes)
    /* const segmentCount = 8;
    const totalWidth = 4.4 * s;
    const segW = totalWidth / segmentCount;
    // Centering logic based on your stadium offset (-0.15)
    const startX = -0.2 - totalWidth / 2 + segW / 2;

    for (let i = 0; i < segmentCount; i++) {
      const posX = startX + i * segW;

      // Assign score: e.g., 100 for corners, 20 for middle stripes
      const pointValue = i === 0 || i === segmentCount - 1 ? 100 : 20;

      this.addBoxCollider(posX, 1.82 * s, 0.1 * s - 1.2 / 2, segW, 0.15, 1.3, {
        name: `bar_segment_${i}`,
        points: pointValue,
        restitution: 0.7,
      });
    } */
    // 3. SIDE POSTS (Full-sized vertical segments)
    const postHeight = 1.8 * s; // Height of the post
    const postThickness = 0.15; // Match the crossbar thickness
    const postDepth = 0.3; // Match the depth for consistent collision

    // Left Post Segment
    this.addBoxCollider(
      -2.35 * s, // X Position
      postHeight / 2, // Y Position (Center height)
      0.1 * s - postDepth / 2 + 0.05, // Z Position (Aligned with Crossbar)
      postThickness, // Width
      postHeight, // Height
      postDepth, // Depth
      {
        name: 'bar_segment_left',
        points: 50,
        restitution: 0.8,
      }
    );

    // Right Post Segment
    this.addBoxCollider(
      1.97 * s, // X Position
      postHeight / 2, // Y Position
      0.1 * s - postDepth / 2, // Z Position
      postThickness, // Width
      postHeight, // Height
      postDepth, // Depth
      {
        name: 'bar_segment_right',
        points: 50,
        restitution: 0.8,
      }
    );
  }
  setupPhysics2() {
    if (!this.physicsWorld) return;
    const s = Global.scaleFact;

    // GROUND
    this.addBoxCollider(0, -0.05, 0, 200, 0.1, 200, { name: 'floor' });

    // BACK OF NET (Deepest part of goal)
    this.addBoxCollider(-0.15, 0.9 * s, -1.35 * s, 4.3 * s, 1.85 * s, 0.4, {
      name: 'netBack',
    });

    // LEFT SIDE (Can detect Bar if ball.z is near the front edge)
    this.addBoxCollider(-2.35 * s, 0.9 * s, -0.5 * s, 0.15, 1.8 * s, 1.3 * s, {
      name: 'netLeft',
      restitution: 0.6, // Slightly bouncy to simulate the post-frame
    });

    // RIGHT SIDE
    this.addBoxCollider(1.97 * s, 0.9 * s, -0.5 * s, 0.15, 1.8 * s, 1.3 * s, {
      name: 'netRight',
      restitution: 0.6,
    });

    // // TOP SIDE (Crossbar detection zone)
    this.addBoxCollider(-0.15, 1.82 * s, -0.5 * s, 4.4 * s, 0.15, 1.3 * s, {
      name: 'netTop',
      restitution: 0.6,
    });
  }

  // ... (Remaining animation/VFX/Dispose methods preserved)

  setupCameraFlashes() {
    this.cameraFlash = Global.assets['glbs']['flash']['asset'].scene;
    this.scene.add(this.cameraFlash);
    this.cameraFlash.position.set(-0.85, 0, -52.7 * Global.scaleFact * 1.22);
    this.cameraFlash.rotation.y = Math.PI;
    this.cameraFlash.scale.set(
      Global.scaleFact,
      Global.scaleFact,
      Global.scaleFact
    );

    this.cameraFlash.traverse((flash) => {
      if (flash.isMesh) {
        flash.envMapIntensity = 2;
        this.flashScale = { value: 5 };
        flash.userData.flashScale = this.flashScale;
        flash.scale.set(0, 0, 0);

        gsap.to(this.flashScale, {
          value: 0,
          delay: Math.random(),
          onUpdate: () =>
            flash.scale.set(
              this.flashScale.value,
              this.flashScale.value,
              this.flashScale.value
            ),
          repeatDelay: Math.max(0.5, Math.random()) * 4,
          repeat: -1,
          duration: 0.1,
        });
      }
    });
  }

  showDefendEffects() {
    this.cameraFlash.traverse((flash, index) => {
      if (flash.isMesh) {
        this.flashScale = { value: 5 };
        this[`defendFlashTwn${index}`] = gsap.to(this.flashScale, {
          value: 0,
          repeat: 4,
          duration: 0.125,
          onUpdate: () =>
            flash.scale.set(
              this.flashScale.value,
              this.flashScale.value,
              this.flashScale.value
            ),
        });
      }
    });
  }

  stopDefendAnim() {
    // this.cameraFlash.traverse((_, index) => {
    //   if (this[`defendFlashTwn${index}`]) this[`defendFlashTwn${index}`].kill();
    // });
  }

  update(data) {}

  updatePostFeature() {
    gsap.delayedCall(
      Global.gameTry == 1 ? Global.cameraPanTime * 0.5 : 0,
      () => {
        if (this.postMesh) {
          this.postMesh.material.envMapIntensity = 10;
          this.postMesh.material.opacity = 0.9;
        }
      }
    );
  }

  dispose() {
    if (this.physicsWorld) {
      this._rigidBodies.forEach((body) =>
        this.physicsWorld.removeRigidBody(body)
      );
      this._rigidBodies = [];
    }
    this._debugMeshes.forEach((mesh) => {
      mesh.geometry.dispose();
      mesh.material.dispose();
      this.scene.remove(mesh);
    });
    this._debugMeshes = [];
    [this.cameraFlash, this.stadium].forEach((obj) => {
      if (!obj) return;
      obj.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            const materials = Array.isArray(child.material)
              ? child.material
              : [child.material];
            materials.forEach((m) => m.dispose());
          }
        }
      });
      this.scene.remove(obj);
    });

    if (this.events) {
      this.events.removeEventListener('GAMESCENE_EVENTS', this.onEvents);
      this.events.removeEventListener('PLAYER_EVENTS', this.onEvents);
      this.events.removeEventListener('GAME_EVENTS', this.onEvents);
    }
  }
}
